import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
// Utils
// @ts-ignore
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
// @ts-ignore
import {
  Button, Formblock, Message, Select,
} from '@zolteam/zol-admin-ui';
// Services
import { useMemo, useState, useEffect } from 'react';
import { createCustomer, updateCustomer } from '../../services/customers/customersService';
import { useGetCustomerListQuery } from '../../services/customers/customersQueries';
// Locales
import fr from '../../locales/fr.json';
import { useGetAdminListQuery } from '../../services/users/usersQueries';
import { CUSTOMER_STATUS } from '../../domain/customers/customersUseCases';

const FORM_NAME = 'newCustomer';

/**
 * UserForm component.
 * @returns {JSX.Element}
 */
function CustomerNew() {
  const { email } = useParams();
  // local states
  const [broker, setBroker] = useState(null);
  const [status, setStatus] = useState('validate');
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  // schema
  const SCHEMA = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Customer',
    properties: {
      firstname: {
        type: 'string',
        description: t('customerList.columns.firstname'),
        x_layout: '1/1/1/7',
        minLength: '1',
      },
      lastname: {
        type: 'string',
        description: t('customerList.columns.lastname'),
        x_layout: '1/7/1/13',
        minLength: '1',
      },
      email: {
        type: 'string',
        format: 'email',
        description: t('customerList.columns.email'),
        x_layout: '2/1/2/13',
        minLength: '1',
      },
      phone: {
        type: 'string',
        description: t('customerList.columns.phone'),
        x_layout: '3/1/3/13',
        format: 'phone',
        minLength: '1',
      },
      company: {
        type: 'string',
        description: t('customerList.columns.company'),
        x_layout: '4/1/4/13',
      },
    },
    required: ['firstname', 'lastname', 'email', 'phone'],
    additionalProperties: false,
  };

  // API Calls
  const { data: admins } = useGetAdminListQuery({
    pageNumber: 1,
    pageSize: 100,
  });

  const { data: customers } = useGetCustomerListQuery({
    pageNumber: 1,
    pageSize: 1,
    search: email,
  }, { enabled: !!email });

  const customer = useMemo(() => customers?.data?.[0], [customers]);

  const postCreateUserMutation = useMutation(createCustomer, {
    onSuccess: () => {
      navigate(localStorage.getItem('customerListUrl') || '/customers');
    },
  });

  const putUpdateUserMutation = useMutation(updateCustomer, {
    onSuccess: () => {
      navigate(localStorage.getItem('customerListUrl') || '/customers');
    },
  });

  // lifecycle
  const adminOptions = useMemo(() => (admins?.data?.map(
    /**
     * @param {User} admin
     * @returns {{label: string, value: number}}
     */
    ({ firstname, lastname, id }) => ({ label: `${firstname} ${lastname}`, value: id }),
  ) || []), [admins]);

  const defaultValues = useMemo(() => ({
    firstname: customer?.firstname || '',
    lastname: customer?.lastname || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    company: customer?.company || '',
    status: customer?.status || 'validate',
    // make sure that value is an integer
    brokerId: customer?.broker?.id ? parseInt(customer.broker.id?.toString(), 10) : null,
  }), [customer, customers]);

  useEffect(() => {
    if (customer) {
      setBroker(customer.broker ? customer.broker.id : '');
      setStatus(customer.status || 'validate');
    }
  }, [customer]);

  // Handlers
  /**
   * Handles the form submission to create or update a user.
   * @param {object} formValues - The values from the form.
   * @param {string} formValues.email - The email address of the new user.
   * @param {string} formValues.firstname - The first name of the new user.
   * @param {string} formValues.lastname - The last name of the new user.
   * @param {string} formValues.phone - The last name of the new user.
   * @param {string} formValues.company - The last name of the new user.
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    /** @type {Customer} */
    const body = {
      email: formValues.email,
      firstname: formValues.firstname,
      lastname: formValues.lastname,
      phone: formValues.phone,
      company: formValues.company,
      brokerId: broker,
      status,
    };
    if (email) {
      putUpdateUserMutation.mutate({ ...body, id: customer.id });
    } else {
      postCreateUserMutation.mutate(body);
    }
  };

  /**
   * Set the broker.
   * @param {string} newBroker - The selected broker.
   * @param {any} formInfo
   */
  const handleSetBroker = (newBroker, formInfo) => {
    setBroker(parseInt(newBroker, 10));
    formInfo.formMethods.setValue('brokerId', newBroker ? parseInt(newBroker, 10) : null, { shouldValidate: true });
  };

  /**
   * Set the status.
   * @param {{value: string, label: string}} newStatus - The selected status.
   */
  const handleSetStatus = (newStatus) => {
    setStatus(newStatus.value);
  };

  return (
    <div className="padding-l">
      <h1 className="h1-default margin-bottom-m" style={{ fontWeight: 'bold' }}>
        {email ? t('editCustomer.title') : t('newCustomer.title')}
      </h1>
      {postCreateUserMutation.isError || putUpdateUserMutation.isError ? (
        <div className="margin-bottom-m">
          <Message>
            {t(`apiErrors.${postCreateUserMutation.error
            // @ts-ignore
              ?.code || putUpdateUserMutation.error?.code}`)}
          </Message>
        </div>
      ) : null}
      {(email && customer) || !email ? (
        <Form
          name={FORM_NAME}
          className=""
          callbacks={{
            onSubmit,
          }}
          schema={SCHEMA}
          translations={{
            formValidation: fr.formValidation,
          }}
          defaultValues={defaultValues}
        >
          {
        (formConfig, formInfo) => (
          <div>
            <div className="d-flex f-row f-wrap gap-m">
              <Formblock
                key="broker"
                htmlFor="broker"
                className="d-flex margin-bottom-xs"
              >
                <label htmlFor="broker" className="p2-default">
                  <span>{t('customerList.fields.broker')}</span>
                  <Select
                  // @ts-ignore
                    id="broker"
                    name="broker"
                    placeholder={t('customerList.fields.broker')}
                    options={adminOptions}
                    value={broker || ''}
                    onChange={
                    (
                      /** @type {{ target: { value: string }}} */ e,
                    ) => handleSetBroker(e?.target?.value, formInfo)
                  }
                  />
                </label>
              </Formblock>
              <Formblock
                key="status"
                htmlFor="status"
                className="d-flex margin-bottom-xs"
              >
                <label htmlFor="status" className="p2-default">
                  <span>{t('customerList.columns.status')}</span>
                  <Select
                  // @ts-ignore
                    id="status"
                    name="status"
                    placeholder={t('customerList.columns.status')}
                    options={CUSTOMER_STATUS}
                    value={status}
                    onChange={
                    (
                      /** @type {{ target: { value: { value: string; label: string; }; }; }} */ e,
                    ) => handleSetStatus(e?.target?.value)
                  }
                  />
                </label>
              </Formblock>
            </div>
            <FormElements
              translations={null}
              formConfig={formConfig}
              formTitle={formInfo.formName}
            />
          </div>
        )
}
        </Form>
      ) : null}

      <Button
        form={FORM_NAME}
        isSubmit
        isLoading={postCreateUserMutation.isLoading || putUpdateUserMutation.isLoading}
      >
        {email ? t('editCustomer.actions.submit') : t('newCustomer.actions.submit')}
      </Button>
    </div>
  );
}

export default CustomerNew;
